import React from 'react';
import * as Recharts from 'recharts';
import { GROWTH, daysBetween, makeDate } from './data.jsx';
import { Icon } from './icons.jsx';
import { billingService, isRealTenantsMode } from './services.jsx';
// Dashboard page
const { useState, useEffect, useMemo } = React;

const fmtBRL = (v) => 'R$ ' + Math.round(v).toLocaleString('pt-BR');

const normDashStatus = (s) => {
  const map = { ativo: 'active', inadimplente: 'overdue', past_due: 'overdue', suspenso: 'suspended', trial: 'trial', active: 'active', overdue: 'overdue', suspended: 'suspended' };
  return map[s] || s;
};

const GrowthChart = ({ data, metric, theme }) => {
  const { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } = Recharts;
  const navy = theme === 'dark' ? '#7aa3d8' : '#1B3D60';

  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={navy} stopOpacity={0.22} />
              <stop offset="100%" stopColor={navy} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={theme === 'dark' ? '#1e2b44' : '#eef0f3'} vertical={false} />
          <XAxis dataKey="month" tick={{ fill: theme === 'dark' ? '#a6b0c2' : '#8a94a6', fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: theme === 'dark' ? '#a6b0c2' : '#8a94a6', fontSize: 11 }} axisLine={false} tickLine={false}
            tickFormatter={v => metric === 'mrr' ? 'R$' + (v/1000).toFixed(0) + 'k' : v} />
          <Tooltip
            contentStyle={{ background: theme === 'dark' ? '#111a2e' : '#fff', border: '1px solid ' + (theme === 'dark' ? '#1e2b44' : '#e6e8eb'), borderRadius: 8, fontSize: 12 }}
            formatter={(v) => metric === 'mrr' ? [fmtBRL(v), 'MRR'] : [v, 'Lojas']}
            labelStyle={{ color: theme === 'dark' ? '#e8ecf3' : '#0a1628', fontWeight: 600, fontSize: 12, marginBottom: 4 }}
          />
          <Area type="monotone" dataKey={metric} stroke={navy} strokeWidth={2} fill="url(#grad)" dot={{ fill: navy, r: 3 }} activeDot={{ r: 5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const Dashboard = ({ onNav, theme, stores: propStores, storesLoading, storesError, onRetryStores }) => {
  const useReal = typeof isRealTenantsMode === 'function' && isRealTenantsMode();
  const stores = propStores || [];

  const [chartMetric, setChartMetric] = useState('stores');
  const [billingOverview, setBillingOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(useReal);
  const [overviewError, setOverviewError] = useState(null);

  useEffect(() => {
    if (!useReal || !billingService?.getOverview) {
      setOverviewLoading(false);
      return;
    }
    let cancelled = false;
    setOverviewLoading(true);
    billingService.getOverview()
      .then((data) => { if (!cancelled) setBillingOverview(data); })
      .catch((e) => { if (!cancelled) setOverviewError(e?.message || 'Erro ao carregar billing.'); })
      .finally(() => { if (!cancelled) setOverviewLoading(false); });
    return () => { cancelled = true; };
  }, [useReal]);

  const totals = billingOverview?.totals || billingOverview || {};
  const totalStores = useReal ? (totals.tenants ?? stores.length) : stores.length;

  const kpisLive = useMemo(() => {
    if (useReal && billingOverview) {
      return {
        total: totals.tenants ?? stores.length,
        ativas: totals.active ?? 0,
        trial: totals.trial ?? 0,
        inadimplentes: totals.past_due ?? 0,
        suspensos: totals.suspended ?? 0,
        mrr: null,
      };
    }
    return {
      total: stores.length,
      ativas: stores.filter(s => normDashStatus(s.status) === 'active').length,
      trial: stores.filter(s => normDashStatus(s.status) === 'trial').length,
      inadimplentes: stores.filter(s => ['overdue', 'inadimplente'].includes(normDashStatus(s.status))).length,
      suspensos: stores.filter(s => normDashStatus(s.status) === 'suspended').length,
      mrr: stores.reduce((a, b) => a + (b.mrr || 0), 0),
    };
  }, [stores, useReal, billingOverview, totals]);

  const alertsLive = useMemo(() => {
    if (useReal) {
      return stores
        .filter(s => ['overdue', 'inadimplente', 'past_due'].includes(normDashStatus(s.status)))
        .slice(0, 3)
        .map(s => ({
          type: 'danger', icon: 'overdue',
          title: `${s.name} — status comercial: inadimplente`,
          meta: `Plano ${s.plan?.name || '—'}`,
          storeId: s.id,
        }));
    }
    return [
      ...stores.filter(s => s.status === 'inadimplente').slice(0, 3).map(s => ({
        type: 'danger', icon: 'overdue',
        title: `${s.name} está com pagamento atrasado há ${Math.abs(daysBetween(s.nextDue, makeDate(0)))} dias`,
        meta: `Plano ${s.plan.name} · R$ ${s.plan.price.toFixed(2).replace('.', ',')}`,
        storeId: s.id,
      })),
      ...stores.filter(s => s.status === 'trial' && daysBetween(makeDate(0), s.nextDue) <= 5).slice(0, 2).map(s => ({
        type: 'warning', icon: 'trial',
        title: `${s.name} termina o trial em ${daysBetween(makeDate(0), s.nextDue)} dias`,
        meta: 'Sem método de pagamento cadastrado',
        storeId: s.id,
      })),
      ...stores.filter(s => s.status === 'suspenso').slice(0, 1).map(s => ({
        type: 'info', icon: 'suspended',
        title: `${s.name} foi suspensa`,
        meta: 'Suspensa · motivo: inadimplência',
        storeId: s.id,
      })),
    ];
  }, [stores, useReal]);

  const kpis = [
    { label: 'Total de lojas', value: storesLoading ? '…' : kpisLive.total, delta: storesLoading ? '' : `${kpisLive.ativas} ativas`, icon: 'store' },
    { label: 'Lojas ativas', value: storesLoading ? '…' : kpisLive.ativas, delta: totalStores ? `${Math.round((kpisLive.ativas / Math.max(totalStores, 1)) * 100)}% da base` : '—', icon: 'check' },
    { label: 'Em trial', value: storesLoading ? '…' : kpisLive.trial, delta: kpisLive.trial > 0 ? `${Math.round(kpisLive.trial / Math.max(totalStores, 1) * 100)}% do total` : 'Nenhuma', deltaDir: 'flat', icon: 'trial' },
    { label: 'Inadimplentes', value: storesLoading ? '…' : kpisLive.inadimplentes, delta: kpisLive.inadimplentes > 0 ? 'Requer atenção' : 'Tudo OK', deltaDir: kpisLive.inadimplentes > 0 ? 'down' : 'up', icon: 'overdue', accent: kpisLive.inadimplentes > 0 },
    { label: useReal ? 'Suspensos' : 'MRR total', value: useReal ? (storesLoading ? '…' : kpisLive.suspensos) : fmtBRL(kpisLive.mrr || 0), delta: useReal ? 'Status comercial' : `${kpisLive.ativas} assinaturas ativas`, deltaDir: 'up', icon: useReal ? 'suspend' : 'trending' },
  ];

  const planDistribution = useMemo(() => {
    const ids = ['basic', 'pro'];
    return ids.map(id => ({
      id,
      name: id === 'basic' ? 'Basic' : 'Pro',
      price: id === 'basic' ? 199 : 499,
      count: stores.filter(s => s.plan?.id === id).length,
    }));
  }, [stores]);

  if (useReal && storesError && !storesLoading && !stores.length) {
    return (
      <div className="content">
        <div className="page-header"><div><h1>Dashboard</h1><p className="sub">Visão geral da plataforma</p></div></div>
        <div style={{ padding: 24, background: 'var(--danger-bg)', borderRadius: 12, color: 'var(--danger)' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Não foi possível carregar dados reais</div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>{storesError}</div>
          {onRetryStores && <button type="button" className="btn" onClick={onRetryStores}>Tentar novamente</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="sub">Visão geral da plataforma{useReal ? ' · dados da API' : ''}</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => (useReal && onRetryStores ? onRetryStores() : window.location.reload())}>
            <Icon name="refresh" size={14} />Atualizar
          </button>
        </div>
      </div>

      {(storesLoading || overviewLoading) && useReal && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Carregando dados reais…</div>
      )}
      {overviewError && useReal && (
        <div style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 12 }}>Billing overview: {overviewError}</div>
      )}

      <div className="kpi-grid">
        {kpis.map((k, i) => (
          <div key={i} className={'kpi ' + (k.accent ? 'accent-red' : '')}>
            <div className="label"><Icon name={k.icon} size={13} />{k.label}</div>
            <div className="value">{k.value}</div>
            {k.delta && (
              <div className={'delta ' + (k.deltaDir || '')}>
                {k.deltaDir === 'up' && <Icon name="arrowUp" size={12} />}
                {k.deltaDir === 'down' && <Icon name="arrowDown" size={12} />}
                {k.delta}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={useReal ? '' : 'grid-2'} style={useReal ? { display: 'grid', gridTemplateColumns: '1fr', gap: 16 } : undefined}>
        {!useReal && (
          <div className="card">
            <div className="card-head">
              <h3>Crescimento</h3>
              <span className="sub">últimos 6 meses</span>
              <div className="actions">
                <div className="seg" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, background: 'var(--bg-subtle)', padding: 3, borderRadius: 8 }}>
                  <button onClick={() => setChartMetric('stores')} style={{ height: 26, padding: '0 10px', border: 'none', background: chartMetric === 'stores' ? 'var(--bg-elevated)' : 'transparent', color: chartMetric === 'stores' ? 'var(--text)' : 'var(--text-secondary)', borderRadius: 5, fontSize: 12, fontWeight: 500, boxShadow: chartMetric === 'stores' ? 'var(--shadow-xs)' : 'none' }}>Lojas</button>
                  <button onClick={() => setChartMetric('mrr')} style={{ height: 26, padding: '0 10px', border: 'none', background: chartMetric === 'mrr' ? 'var(--bg-elevated)' : 'transparent', color: chartMetric === 'mrr' ? 'var(--text)' : 'var(--text-secondary)', borderRadius: 5, fontSize: 12, fontWeight: 500, boxShadow: chartMetric === 'mrr' ? 'var(--shadow-xs)' : 'none' }}>MRR</button>
                </div>
              </div>
            </div>
            <div className="chart-wrap">
              <GrowthChart data={GROWTH} metric={chartMetric} theme={theme} />
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-head">
            <h3>Distribuição por plano</h3>
            <span className="sub">{storesLoading ? '…' : `${stores.length} lojas`}</span>
          </div>
          <div style={{ padding: '16px 20px 20px' }}>
            {planDistribution.map(p => {
              const pct = stores.length > 0 ? Math.round(p.count / stores.length * 100) : 0;
              return (
                <div key={p.id} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                    <span style={{ fontWeight: 500 }}>{p.name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {fmtBRL(p.price)}</span></span>
                    <span style={{ color: 'var(--text-muted)' }}>{p.count} lojas · {pct}%</span>
                  </div>
                  <div className="usage-bar"><div className="fill" style={{ width: pct + '%' }} /></div>
                </div>
              );
            })}
            {!stores.length && !storesLoading && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nenhuma loja carregada.</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="card">
          <div className="card-head">
            <h3>Alertas operacionais</h3>
            <span className="sub">{alertsLive.length} itens que precisam de atenção</span>
            <div className="actions"></div>
          </div>
          <div className="alert-list">
            {alertsLive.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhum alerta no momento.</div>
            )}
            {alertsLive.map((a, i) => {
              const store = stores.find(s => s.id === a.storeId);
              return (
                <div key={i} className="alert-row" onClick={() => store && onNav({ page: 'loja', store })} style={{ cursor: store ? 'pointer' : 'default' }}>
                  <div className={`alert-icon ${a.type}`}><Icon name={a.icon} size={16} /></div>
                  <div className="alert-body">
                    <div className="alert-title">{a.title}</div>
                    <div className="alert-meta">{a.meta}</div>
                  </div>
                  {store && <Icon name="arrowRight" size={14} className="alert-action" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export {
  Dashboard,
};
